import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { faCheckToSlot } from '@fortawesome/pro-regular-svg-icons/faCheckToSlot';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Col, Flex, Form, Space, theme } from 'antd';
import dayjs from 'dayjs';
import { PosModal } from 'pos-client/components/shared/Display/Modal/PosModal.tsx';
import { PosInputNumber } from 'pos-client/components/shared/Form/PosInputNumber.tsx';
import { PosSelect } from 'pos-client/components/shared/Form/PosSelect.tsx';
import {
  PosTimePicker,
  type PosTimePickerProps,
} from 'pos-client/components/shared/Form/PosTimePicker.tsx';
import { PosColumn } from 'pos-client/components/shared/Layout/Grid/PosColumn.tsx';
import { PosGrid } from 'pos-client/components/shared/Layout/Grid/PosGrid.tsx';
import { useConfirmModal } from 'pos-client/hooks/useConfirmModal.tsx';
import { useNotifications } from 'pos-client/hooks/useNotifications.tsx';
import { fullWidth } from 'pos-client/utils/cssHelpers.ts';
import { trpc } from 'pos-client/utils/trpc.ts';
import {
  type DropOffsFormModel,
  DropOffsFormSchema,
  type DropOffsModel,
} from 'pos-shared/models/openingHoursGroupSchema.ts';

import { useWeekdays } from './useWeekdays.tsx';

const MAX_LENGTH = 24;
const MIN_LENGTH = 1;

interface Props {
  data?: Partial<DropOffsModel> | undefined;
  handleOk: () => void;
  handleCancel: () => void;
}

export const CreateEditDropOffsModal = ({
  data,
  handleOk,
  handleCancel,
}: Props) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const mode = data ? 'edit' : 'create';
  const weekdays = useWeekdays();
  const { token } = theme.useToken();

  const { showSuccess, showError } = useNotifications();

  const {
    control,
    handleSubmit,
    watch,
    formState: { isDirty },
  } = useForm<DropOffsFormModel>({
    defaultValues: {
      id: '',
      dayOfTheWeek: '',
      flexibility: 'AllDay',
      length: 0,
      ...(data && {
        ...data,
        time: dayjs(data.time, 'HH:mm:ss'),
      }),
    },
    resolver: zodResolver(DropOffsFormSchema),
  });

  const { getAllDropOffs } = trpc.useContext();
  const [enableSubmit, setEnableSubmit] = useState(false);

  const modalTitle =
    mode === 'create'
      ? t('locations.trading.createDropOff')
      : t('locations.trading.updateDropOff');

  const { showModal: showUnsavedChangesModal } = useConfirmModal({
    type: 'delete',
    title: t('common.confirmCancel.unsavedChanges'),
    content: t('common.confirmCancel.unsavedChangesConfirm'),
    confirmText: t('common.buttons.discardChanges'),
    confirmProps: {
      ghost: true,
    },
    onConfirm: handleCancel,
  });

  const onCancelModal = () => {
    if (isDirty) {
      showUnsavedChangesModal();
    } else {
      handleCancel();
    }
  };

  const onSuccessMutation = async () => {
    await getAllDropOffs.invalidate();
    await handleOk();
  };

  const { mutate: createDropOffs, isLoading: isCreating } =
    trpc.createDropOffs.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'created',
            entity: 'drop-off',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'create',
            entity: 'drop-off',
          })
        );
      },
    });
  const { mutate: updateDropOffs, isLoading: isUpdating } =
    trpc.updateDropOffs.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'updated',
            entity: 'drop-off',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'update',
            entity: 'drop-off',
          })
        );
      },
    });
  const { mutate: deleteDropOffs, isLoading: isDeleting } =
    trpc.deleteDropOffs.useMutation({
      onSuccess: async () => {
        onSuccessMutation();
        showSuccess(
          t('common.notification.success.message', {
            action: 'deleted',
            entity: 'drop-off',
          })
        );
      },
      onError: async () => {
        showError(
          t('common.notification.error.message', {
            action: 'delete',
            entity: 'drop-off',
          })
        );
      },
    });

  const saveDropOffs = handleSubmit((formData) => {
    const formattedData = {
      ...formData,
      time: formData.time.format('HH:mm:ss'),
    };

    if (mode === 'edit') {
      updateDropOffs({
        openingHoursId: id || '',
        dropOffsId: formData?.id || '',
        data: formattedData,
      });
    } else {
      createDropOffs({ openingHoursId: id || '', data: formattedData });
    }
  });

  const { showModal: confirmRemoveDropOff } = useConfirmModal({
    type: 'delete',
    title: t('locations.trading.removeDropOff'),
    content: t('locations.trading.removeDropOffConfirm'),
    confirmText: t('common.buttons.delete'),
    confirmProps: {
      ghost: true,
    },
  });

  const handleRemoveDropOffs = () => {
    confirmRemoveDropOff({
      onConfirm() {
        deleteDropOffs({
          openingHoursId: id || '',
          dropOffsId: data?.id || '',
        });
      },
    });
  };

  const flexibilityValues = () => {
    const values = ['AllDay', 'Am', 'Pm', 'Fixed'];
    return values.map((item) => ({ value: item, label: item }));
  };

  const defaultTimePickerProps: Partial<PosTimePickerProps> = {
    use12Hours: true,
    showNow: false,
    needConfirm: true,
    style: fullWidth,
    allowClear: false,
  };

  useEffect(() => {
    const selectedValue = watch((value) => {
      if (typeof value !== 'undefined') {
        const hasDayOfTheWeek = value?.dayOfTheWeek;
        const hasTime = value?.time;
        const hasLength =
          value?.length !== null && value?.length !== undefined
            ? value?.length >= MIN_LENGTH || value?.length <= MAX_LENGTH
            : value?.length;
        const hasFlexibility = value?.flexibility;
        setEnableSubmit(
          !!(hasDayOfTheWeek && hasTime && hasLength && hasFlexibility)
        );
      }
    });
    return () => selectedValue.unsubscribe();
  }, [watch]);

  return (
    <PosModal
      header={{ title: modalTitle, icon: faCheckToSlot }}
      open
      onCancel={onCancelModal}
      footer={
        <Flex justify="space-between" style={fullWidth}>
          <Col>
            <Button danger onClick={handleRemoveDropOffs} loading={isDeleting}>
              {t('common.buttons.delete')}
            </Button>
          </Col>
          <Space style={{ flexGrow: 1, justifyContent: 'flex-end' }}>
            <Button type="default" onClick={onCancelModal}>
              {t('common.buttons.cancel')}
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={saveDropOffs}
              loading={isUpdating || isCreating}
              disabled={!isDirty || !enableSubmit}
            >
              {mode === 'edit'
                ? t('common.buttons.update')
                : t('common.buttons.create')}
            </Button>
          </Space>
        </Flex>
      }
    >
      <Form layout="vertical">
        <PosGrid gutter={[0, token.paddingSM]} style={fullWidth}>
          <PosColumn span={24}>
            <PosSelect
              control={control}
              label={t('common.time.unit.day')}
              name="dayOfTheWeek"
              showSearch
              options={weekdays}
              style={fullWidth}
            />
          </PosColumn>
          <PosColumn span={24}>
            <PosSelect
              control={control}
              name="flexibility"
              label={t('locations.trading.flexibility')}
              options={flexibilityValues()}
              showSearch
              style={fullWidth}
            />
          </PosColumn>
          <PosColumn span={24}>
            <PosTimePicker
              control={control}
              name="time"
              label={t('locations.trading.startingTime')}
              {...defaultTimePickerProps}
            />
          </PosColumn>
          <PosColumn span={24}>
            <PosInputNumber
              control={control}
              name="length"
              label={`${t('common.time.duration')} (${t('common.time.unit.hours')})`}
              max={MAX_LENGTH}
              min={MIN_LENGTH}
              style={{ width: '100%' }}
            />
          </PosColumn>
        </PosGrid>
      </Form>
    </PosModal>
  );
};
